// @ts-nocheck
import enUSFile from "src/components/files/translations/en-US.csv";
import enCAFile from "src/components/files/translations/en-CA.csv";
import esMXFile from "src/components/files/translations/es-MX.csv";
import frCAFile from "src/components/files/translations/fr-CA.csv";

import siteFile from "src/components/files/site.csv";
import businessUnitFile from "src/components/files/business_unit.csv";
import localeFile from "src/components/files/locale.csv";
import Papa from "papaparse";
import { DEFAULT_LOCALE, LOCALE_VALUES } from "src/constants";

class ParseService {
  /**
   * Function to parse 'remote' CSV file.
   * @param file The file to parse
   * @param download download set to true indicates that the string you passed as the first
   * argument to parse() is actually a URL from which to download a file and parse its contents
   * @returns {Promise<Array>} An array of data record array which represents a line of data.
   */
  parseCSVFile = async (file, download) =>
    new Promise((resolve) => {
      Papa.parse(file, {
        delimiter: ",",
        comments: true,
        download: download ?? true,
        skipEmptyLines: true,
        complete: (result) => {
          resolve(result.data);
        },
      });
    });

  /**
   * Function to parse CSV files.
   * @param siteCode the site code of the user
   * @param download optional, to be use in parseCSVFile
   * @returns {Promise<Array>} An array of process path pairs, return an empty array  if site is not eligible.
   */
  parseCSVFiles = async (siteCode, download) => {
    // return an array of site record, e.g., [["SEA94","AMZL"], ...]
    const siteRecords = await this.parseCSVFile(siteFile, download);

    const siteRecord = siteRecords.filter(
      ([site]) => site.trim() === siteCode
    )[0];

    // return an empty array if no site found
    if (!siteRecord) return [];

    const targetBusinessUnit = siteRecord[1].trim();
    // array of business unit record, e.g., [["AMZL","Pick","N"], ...]
    const businessUnitRecords = await this.parseCSVFile(
      businessUnitFile,
      download
    );

    const targetBusinessUnitRecords = businessUnitRecords.filter(
      ([businessUnit]) => businessUnit.trim() === targetBusinessUnit
    );

    const processPathPairs = targetBusinessUnitRecords.map((record) =>
      record.slice(1)
    );

    return processPathPairs;
  };

  /**
   * Function to parse locale CSV files.
   * @param siteCode the site code of the user
   * @param download optional, to be use in parseCSVFile
   * @returns {Promise<String>} return a string locale, return DEFAULT_LOCALE(en-US) if site is not eligible.
   */
  parseLocaleCSVFile = async (siteCode, download) => {
    // return an array of siteLocaleRecords record, e.g., [["DXT4","fr-CA"], ...]
    const siteLocaleRecords = await this.parseCSVFile(localeFile, download);

    const targetSiteLocaleRecord = siteLocaleRecords.filter(
      ([site]) => site.trim() === siteCode
    )[0];

    // return an empty array if no site found
    if (!targetSiteLocaleRecord) return DEFAULT_LOCALE;

    return targetSiteLocaleRecord[1];
  };

  /**
   * Function to retrieve process paths.
   * @param approvedPaths the approved process paths
   * @param language language locale (e.g., en-US)
   * @returns {Promise<Array>} return an array of approved paths in anguage locale
   */
  retrieveProcessPathsByLocale = async (approvedPaths, language) => {
    const languageFile = this.pickFileByLanguage(language);
    const processPathsRecords = await this.parseCSVFile(languageFile);
    const approvedPathsInLanguage = processPathsRecords
      .filter(([enUSProcessPath]) => approvedPaths.includes(enUSProcessPath))
      .reduce((result, innerArray) => {
        result.push(innerArray[1]);
        return result;
      }, []);

    return approvedPathsInLanguage;
  };

  pickFileByLanguage = (language) => {
    switch (language) {
      case LOCALE_VALUES.EN_US:
        return enUSFile;
      case LOCALE_VALUES.EN_CA:
        return enCAFile;
      case LOCALE_VALUES.ES_MX:
        return esMXFile;
      case LOCALE_VALUES.FR_CA:
        return frCAFile;
      default:
        return enUSFile;
    }
  };
}

export default new ParseService();
